/**
 * PaymentQRModal — shown when a user selects a paid plan.
 *
 * Flow:
 *   1. Fetches /api/billing/payment-info to get amount, ref, WhatsApp URL
 *   2. Fetches /api/billing/payment-qr  to show the UPI QR image
 *   3. "I've paid" → opens WhatsApp with pre-filled proof message
 *
 * The UPI ID is NEVER exposed to the browser — QR is generated server-side.
 *
 * Props:
 *   open     — boolean
 *   plan     — "pro" | "team" | "business"
 *   onClose  — callback to close modal
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Loader2, CheckCircle, Copy, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_COLORS = {
  pro:      '#00E599',
  team:     '#D946EF',
  business: '#F59E0B',
};

export default function PaymentQRModal({ open, plan, onClose }) {
  const [info, setInfo]       = useState(null);
  const [qrUrl, setQrUrl]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied]   = useState(false);

  useEffect(() => {
    if (!open || !plan) return;
    setLoading(true);
    setInfo(null);
    setQrUrl(null);

    // Fetch payment info (includes the QR as a base64 data URI so the <img>
    // works with Bearer auth — a raw <img src=/payment-qr> can't send the token).
    axios.get(`${API}/billing/payment-info?plan=${plan}`, { withCredentials: true })
      .then((infoRes) => {
        setInfo(infoRes.data);
        setQrUrl(infoRes.data.qr_base64 || null);
      })
      .catch(() => toast.error('Could not load payment details. Please try again.'))
      .finally(() => setLoading(false));
  }, [open, plan]);

  if (!open) return null;

  const color = PLAN_COLORS[plan] || '#00E599';

  const handleCopyRef = () => {
    if (!info?.ref) return;
    navigator.clipboard.writeText(info.ref);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWhatsApp = () => {
    if (info?.whatsapp_url) {
      window.open(info.whatsapp_url, '_blank');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.93 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.93 }}
            transition={{ duration: 0.18 }}
            className="w-full max-w-sm bg-[#0D1117] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
              <div>
                <h2 className="font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {info ? `AutoQA ${info.label} Plan` : 'Loading…'}
                </h2>
                {info && (
                  <p className="text-2xl font-bold mt-0.5" style={{ color }}>
                    ₹{info.amount.toLocaleString('en-IN')}
                    <span className="text-sm font-normal text-slate-400">/month</span>
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="text-slate-500 hover:text-white transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {loading ? (
                <div className="flex flex-col items-center gap-3 py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  <p className="text-slate-400 text-sm">Generating QR code…</p>
                </div>
              ) : (
                <>
                  {/* QR Code */}
                  <div className="flex flex-col items-center mb-5">
                    <div className="bg-white p-3 rounded-xl shadow-lg mb-3">
                      {qrUrl ? (
                        <img
                          src={qrUrl}
                          alt="UPI QR Code"
                          className="w-48 h-48 object-contain"
                        />
                      ) : (
                        <div className="w-48 h-48 flex items-center justify-center bg-slate-100 rounded">
                          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                      )}
                    </div>
                    <p className="text-slate-400 text-xs text-center">
                      Scan with Google Pay, PhonePe, Paytm or any UPI app
                    </p>
                  </div>

                  {/* Payment reference */}
                  {info?.ref && (
                    <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 mb-4">
                      <p className="text-[10px] text-slate-500 mb-1">Payment reference (auto-filled in QR)</p>
                      <div className="flex items-center justify-between gap-2">
                        <code className="text-xs text-slate-300 font-mono truncate">{info.ref}</code>
                        <button
                          onClick={handleCopyRef}
                          className="text-slate-500 hover:text-white transition-colors flex-shrink-0"
                        >
                          {copied ? (
                            <CheckCircle className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Security note */}
                  <div className="flex items-start gap-2 bg-blue-500/5 border border-blue-500/20 rounded-lg px-3 py-2.5 mb-5">
                    <ShieldCheck className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <p className="text-[11px] text-blue-300">
                      Your payment goes directly to Ashish Sharma (AutoQA founder).
                      Plan is activated manually after proof is verified.
                    </p>
                  </div>

                  {/* CTA */}
                  <Button
                    className="w-full h-11 font-semibold text-sm bg-[#25D366] hover:bg-[#20BD5A] text-white"
                    onClick={handleWhatsApp}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    I've paid — Send proof on WhatsApp
                  </Button>
                  <p className="text-center text-[11px] text-slate-600 mt-2">
                    Ashish will activate your plan within minutes of receiving proof
                  </p>
                </>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
